import { Suspense, lazy } from 'react';
import { motion, useScroll, useSpring } from 'framer-motion';
import { HiMusicalNote, HiUser, HiVideoCamera, HiEnvelope, HiRadio } from 'react-icons/hi2';
import Hero from '@/components/Hero';
import { ErrorBoundary } from '@/components/ErrorBoundary';

const Music = lazy(() => import('@/components/Music'));
const LoFi = lazy(() => import('@/components/LoFi'));
const About = lazy(() => import('@/components/About'));
const WhyAIMusic = lazy(() => import('@/components/WhyAIMusic'));
const Videos = lazy(() => import('@/components/Videos'));
const Contact = lazy(() => import('@/components/Contact'));
const Footer = lazy(() => import('@/components/Footer'));
const CursorGlow = lazy(() => import('@/components/ui/CursorGlow'));

function SectionSkeleton() {
  return (
    <div className="min-h-[50vh] bg-cyber-darker animate-pulse flex items-center justify-center">
      <div className="w-16 h-16 border-4 border-cyber-cyan/30 border-t-cyber-cyan rounded-full animate-spin" />
    </div>
  );
}

const navItems = [
  { name: 'Music', icon: HiMusicalNote },
  { name: 'Lo-Fi', icon: HiRadio, href: 'lofi' },
  { name: 'About', icon: HiUser },
  { name: 'Videos', icon: HiVideoCamera },
  { name: 'Contact', icon: HiEnvelope },
];

export default function App() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  return (
    <>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:top-4 focus:left-4 focus:px-6 focus:py-3 focus:bg-cyber-cyan focus:text-cyber-darker focus:font-orbitron focus:font-semibold focus:rounded-lg focus:shadow-lg"
      >
        Skip to main content
      </a>

      <main className="relative" id="main-content">
        <motion.div
          className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-cyber-cyan via-cyber-purple to-cyber-pink z-50 origin-left"
          style={{ scaleX }}
          aria-hidden="true"
        />

        <nav className="fixed top-4 left-1/2 -translate-x-1/2 z-40" aria-label="Main navigation">
          <motion.div
            className="flex gap-1 p-1 bg-cyber-darker/80 backdrop-blur-xl rounded-full border border-white/10"
            initial={{ y: -100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
          >
            {navItems.map((item) => (
              <a
                key={item.name}
                href={`#${'href' in item ? item.href : item.name.toLowerCase()}`}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-rajdhani text-white/70 hover:text-cyber-cyan hover:bg-white/5 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-cyber-cyan focus:ring-offset-2 focus:ring-offset-cyber-darker"
                aria-label={`Navigate to ${item.name} section`}
              >
                <item.icon className="w-4 h-4" aria-hidden="true" />
                {item.name}
              </a>
            ))}
          </motion.div>
        </nav>

        <ErrorBoundary>
          <Hero />
        </ErrorBoundary>
        <ErrorBoundary>
          <Suspense fallback={<SectionSkeleton />}>
            <Music />
          </Suspense>
        </ErrorBoundary>
        <ErrorBoundary>
          <Suspense fallback={<SectionSkeleton />}>
            <LoFi />
          </Suspense>
        </ErrorBoundary>
        <ErrorBoundary>
          <Suspense fallback={<SectionSkeleton />}>
            <About />
          </Suspense>
        </ErrorBoundary>
        <ErrorBoundary>
          <Suspense fallback={<SectionSkeleton />}>
            <WhyAIMusic />
          </Suspense>
        </ErrorBoundary>
        <ErrorBoundary>
          <Suspense fallback={<SectionSkeleton />}>
            <Videos />
          </Suspense>
        </ErrorBoundary>
        <ErrorBoundary>
          <Suspense fallback={<SectionSkeleton />}>
            <Contact />
          </Suspense>
        </ErrorBoundary>
        <ErrorBoundary>
          <Suspense fallback={null}>
            <Footer />
          </Suspense>
        </ErrorBoundary>

        <Suspense fallback={null}>
          <CursorGlow />
        </Suspense>
      </main>
    </>
  );
}
